import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import useQuery from "../../hooks/useQuery";

import { shopActions } from "../../redux/shop/shop.reducer";
import { cartActions } from "../../redux/cart/cart.reducer";
import {
  selectCurrentItem,
  selectIsCollectionFetching,
  selectIsItemFetching,
  selectJustInPreview,
  selectCurrentCollection,
  selectCurrentReviewsByProduct,
} from "../../redux/shop/shop.selectors";
import { selectCurrentUser } from "../../redux/user/user.selectors";

import { getCollections } from "../../apis/shop.dashboard.apis";

import { addItem } from "../../redux/cart/cart.utils";

import useStyles, { StyledRating } from "./collection-item-detail.styles";

// import Footer from "../../components/footer";
// import CollectionItem from "../collection-item/collection-item.component";
import Price from "../price";
import Loading from "../loading";
import {
  createReview,
  deleteReview,
  updateReview,
} from "../../apis/shop.services.apis";

export const OptionButton = ({
  label,
  option,
  handleSelectOption,
  selectedOptions,
  inQuickview,
}: {
  label: string;
  option: string;
  handleSelectOption: any;
  selectedOptions: any;
  inQuickview?: boolean;
}) => {
  const matches = useMediaQuery("(min-width:1200px)");

  const [selected, setSelected] = useState(option === selectedOptions[label]);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setSelected(option === selectedOptions[label]);
  }, [selectedOptions]);

  const classes = useStyles({
    selected,
    disabled,
    inQuickview,
    matches,
  });

  return (
    <button
      key={option}
      name={label}
      value={option}
      onClick={handleSelectOption}
      className={classes.option}
      disabled={disabled}
    >
      {option}
    </button>
  );
};

const InfoTabTitle = (props: any) => {
  const matches = useMediaQuery("(min-width:1200px)");

  const { infoTab, selectedTab, setSelectedTab } = props;

  const [isTabSelected, setIsTabSelected] = useState(
    infoTab.type === selectedTab,
  );

  useEffect(() => {
    setIsTabSelected(infoTab.type === selectedTab);
  }, [selectedTab]);

  const classes = useStyles({ isTabSelected, matches });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value;

    setSelectedTab(value);
  };

  return (
    <button
      className={classes.descriptionTitle}
      onClick={handleClick}
      value={infoTab.type}
    >
      {infoTab.title}
    </button>
  );
};

const InfoTabContent = (props: any) => {
  const matches = useMediaQuery("(min-width:1200px)");

  const { infoTab, selectedTab } = props;

  const [isTabSelected, setIsTabSelected] = useState(
    infoTab.type === selectedTab,
  );

  useEffect(() => {
    setIsTabSelected(infoTab.type === selectedTab);
  }, [selectedTab]);

  const classes = useStyles({ isTabSelected, matches });

  return (
    <div
      className={classes.descriptionContent}
      dangerouslySetInnerHTML={{
        __html: infoTab.content,
      }}
    />
  );
};

const validationSchema = yup.object().shape({
  rate: yup.string().required(),
  bodyText: yup.string().optional(),
  reviewImageList: yup.array().of(yup.string()),
});

const CollectionItemDetail = () => {
  const dispatch = useDispatch();
  const query = useQuery();

  const matches = useMediaQuery("(min-width:1200px)");

  const currentUser = useSelector(selectCurrentUser);
  const item = useSelector(selectCurrentItem);
  const currentReviews = useSelector(selectCurrentReviewsByProduct);

  const {
    reviews,
    images: reviewImages,
    count: reviewCount,
    averageRate,
  } = currentReviews;

  const productId = query.get("productId") || "";

  useEffect(() => {
    dispatch({
      type: shopActions.fetchItemStart.type,
      payload: productId,
    });
    dispatch({
      type: shopActions.fetchReviewsByProductStart.type,
      payload: productId,
    });
  }, [productId]);

  const collectionId = query.get("collectionId") || "";

  useEffect(() => {
    dispatch({
      type: shopActions.fetchCollectionStart.type,
      payload: collectionId,
    });
  }, [collectionId]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, [item]);

  useEffect(() => {
    let initialOptions: {
      [key: string]: {};
    } = {};
    item?.variantOptions.forEach((variantOption: any) => {
      initialOptions[variantOption.label] = "";
    });

    setSelectedOptions(initialOptions);
  }, [item]);

  const [selectedOptions, setSelectedOptions] = useState<any>({});

  const [selectedProductOrVariant, setSelectedProductOrVariant] =
    useState<any>(null);

  const handleSelectOption = (event: any) => {
    const { name, value } = event.target;

    setSelectedOptions({
      ...selectedOptions,
      [name]: selectedOptions[name] === value ? "" : value,
    });
  };

  const selectProductOrVariant = () => {
    let tempVariant;

    if (!item?.variantOptions || item?.variantOptions.length === 0) {
      setSelectedProductOrVariant(item);
      return;
    }

    if (
      selectedOptions &&
      Object.values(selectedOptions).some((el) => el === "")
    ) {
      setSelectedProductOrVariant("CHOOSE_OPTION");
      return;
    }

    if (
      item?.variants?.some((variant: any) =>
        _.isEqual(variant?.variantOptionsUsed.variantOptions, selectedOptions),
      )
    ) {
      tempVariant = item?.variants?.find((variant: any) =>
        _.isEqual(variant?.variantOptionsUsed.variantOptions, selectedOptions),
      );
      setSelectedProductOrVariant(tempVariant);
    } else {
      setSelectedProductOrVariant("UNAVAILABLE");
    }
  };

  useEffect(() => {
    selectProductOrVariant();
  }, [selectedOptions, item]);

  const [images, setImages] = useState(
    item?.imageDefault
      ? Array.isArray(item?.imageGallery) && item?.imageGallery?.length !== 0
        ? [item?.imageDefault, ...item?.imageGallery]
        : [item?.imageDefault]
      : Array.isArray(item?.imageGallery) && item?.imageGallery?.length !== 0
      ? [...item?.imageGallery]
      : [],
  );

  useEffect(() => {
    setImages(
      !!selectedProductOrVariant && typeof selectedProductOrVariant !== "string"
        ? selectedProductOrVariant.imageDefault ??
          (Array.isArray(selectedProductOrVariant?.imageGallery) &&
            selectedProductOrVariant?.imageGallery?.length !== 0)
          ? [
              selectedProductOrVariant?.imageDefault,
              ...selectedProductOrVariant?.imageGallery,
            ]
          : [selectedProductOrVariant?.imageDefault]
        : Array.isArray(selectedProductOrVariant?.imageGallery) &&
          selectedProductOrVariant?.imageGallery?.length !== 0
        ? [...selectedProductOrVariant?.imageGallery]
        : item?.imageDefault
        ? Array.isArray(item?.imageGallery) && item?.imageGallery?.length !== 0
          ? [item?.imageDefault, ...item?.imageGallery]
          : [item?.imageDefault]
        : Array.isArray(item?.imageGallery) && item?.imageGallery?.length !== 0
        ? [...item?.imageGallery]
        : [],
    );

    setCurrentImageStep(0);
  }, [item, selectedProductOrVariant]);

  const isItemFetching = useSelector(selectIsItemFetching);

  const [isLoading, setIsLoading] = useState(true);

  const [isCategoryLoading, setIsCategoryLoading] = useState(true);

  useEffect(() => {
    setIsLoading(isItemFetching);
  }, [isItemFetching]);

  const collection = useSelector(selectCurrentCollection);

  const [categoryIdList, setCategoryIdList] = useState<any[]>([]);

  useEffect(() => {
    if (collection) {
      setCategoryIdList([...collection?.parentCollectionList, collection?.id]);
    }
  }, [collection]);

  const [categoryList, setCategoryList] = useState<any[]>([]);

  useEffect(() => {
    setIsCategoryLoading(true);
    if (categoryIdList.length !== 0) {
      getCollections(categoryIdList).then((data) => {
        setCategoryList(data);
        setIsCategoryLoading(false);
      });
    }
  }, [categoryIdList]);

  const [currentCollectionId, setCurrentCollectionId] = useState(
    query.has("collectionId") ? query.get("collectionId") : "",
  );

  useEffect(() => {
    setCurrentCollectionId(
      query.has("collectionId") ? query.get("collectionId") : "",
    );
  }, [location.search]);

  const fetchCollection = () => {
    dispatch({
      type: shopActions.fetchCollectionStart.type,
      payload: currentCollectionId,
    });
  };

  useEffect(() => {
    if (query.has("collectionId") && !collection) {
      fetchCollection();
    }
  }, [currentCollectionId]);

  const infoTabList: any[] = [];
  const infoTabTypeList = [
    { type: "htmlDesc", title: "Description" },
    { type: "htmlDetail", title: "Detail" },
    { type: "htmlSpec", title: "Specification" },
  ];

  infoTabTypeList.forEach((infoType) => {
    const { type, title } = infoType;

    if (
      item?.[type] &&
      item?.[type] !== "<p><br class='ProseMirror-trailingBreak'></p>" &&
      !infoTabList.some((tab) => tab.type === type)
    ) {
      infoTabList.push({
        type,
        title,
        content: item?.[type],
      });
    }
  });

  const [selectedTab, setSelectedTab] = useState(infoTabList?.[0]?.type);

  useEffect(() => {
    setSelectedTab(infoTabList?.[0]?.type);
  }, [item]);

  const [currentImageStep, setCurrentImageStep] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const [reviewInput] = useState({
    rate: 5,
    bodyText: "",
    reviewImageList: [],
  });

  const {
    register,
    watch,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: reviewInput,
    resolver: yupResolver(validationSchema),
  });

  const values = getValues();
  const { rate, bodyText, reviewImageList } = values;

  watch();

  const handleReviewInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;

    console.log("name", name);
    console.log("value", value);

    if (name === "rate" || name === "bodyText" || name === "reviewImageList") {
      setValue(name, value);
    }
  };

  const handleRemoveReview = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const reviewId = e.currentTarget.value;

    try {
      await deleteReview({
        productId,
        userId: "aidensung",
        reviewId,
      });

      alert("Review removed!");

      dispatch({
        type: shopActions.fetchReviewsByProductStart.type,
        payload: productId,
      });
    } catch (err) {
      alert(err);
    }
  };

  // const handleUpdateReview = async (e: MouseEvent<HTMLButtonElement>) => {
  //   e.preventDefault();
  //   const value = e.currentTarget.value;
  //   const splittedValue = value.split("#");
  //   // const [reviewId, rate, bodyText, review]

  //   try {
  //     //   await updateReview({productId,
  //     // userId: 'aidensung',
  //     // reviewId,
  //     // rate: number;
  //     // bodyText?: string;
  //     // reviewImageList?: string[];});

  //     alert("Review updated!");

  //     dispatch({
  //       type: shopActions.fetchReviewsByProductStart.type,
  //       payload: productId,
  //     });
  //   } catch (e) {
  //     alert(e);
  //   }
  // };

  const handleSubmitReview = async (params: {
    productId: string;
    userId: string;
    rate: number;
    bodyText?: string;
    reviewImageList?: string[];
  }) => {
    try {
      await createReview(params);

      alert("Review created!");

      setValue("bodyText", "");
      setValue("rate", 5);

      dispatch({
        type: shopActions.fetchReviewsByProductStart.type,
        payload: productId,
      });
    } catch (e) {
      alert(e);
    }
  };

  const onSubmit = async () => {
    await handleSubmitReview({
      productId,
      userId: "aidensung",
      rate,
      bodyText,
      reviewImageList,
    });
  };

  const justInPreview = useSelector(selectJustInPreview);
  const isFetching = useSelector(selectIsCollectionFetching);

  const classes = useStyles({
    images,
    currentImageStep,
    isOnSale: item?.isOnSale,
    isSoldOut: item?.isSoldOut || selectedProductOrVariant?.isSoldOut,
    quantity,
    stock: selectedProductOrVariant?.quantity,
    selectedProductOrVariant,
    matches,
  });

  const handleImageClick = (e: any) => {
    const imageId = parseInt(e.target.id, 10);
    setCurrentImageStep(imageId);
  };

  const handleQuantityAdd = () => {
    setQuantity(quantity + 1);
  };

  const handleQuantityRemove = () => {
    if (quantity > 0) {
      setQuantity(quantity - 1);
    }
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (+value >= 0) {
      setQuantity(+value);
    }
  };

  const addItemToCart = () => {
    addItem(
      dispatch,
      currentUser,
      selectedProductOrVariant,
      quantity,
      currentCollectionId,
    );
  };

  const toggleCart = () => {
    dispatch({
      type: cartActions.toggleCartOpen.type,
    });
  };

  return (
    <div className={classes.outerContainer}>
      {isLoading ? (
        <Loading on size="3rem" marginTop="10rem" marginBottom="10rem" />
      ) : item ? (
        <div className={classes.innerContainer}>
          <div className={classes.upperSection}>
            <div className={classes.imagesSection}>
              {images.length === 0 ? (
                <div className={classes.largeImage}>No Image</div>
              ) : (
                <img className={classes.largeImage} />
              )}
              <div className={classes.smallImages}>
                {images.map((image: any, index: number) => (
                  <img
                    key={index}
                    className={classes.smallImage}
                    src={image.url ? image.url : image}
                    onClick={handleImageClick}
                    id={index.toString()}
                    style={{ opacity: currentImageStep === index ? 1 : 0.6 }}
                  />
                ))}
              </div>
              <div className={classes.badgeContainer}>
                {item.isSoldOut && (
                  <span className={`${classes.badge} ${classes.soldoutBadge}`}>
                    Soldout
                  </span>
                )}
                {item.isPopular && (
                  <span className={`${classes.badge} ${classes.popularBadge}`}>
                    Popular
                  </span>
                )}
                {item.isOnSale && (
                  <span className={`${classes.badge} ${classes.onsaleBadge}`}>
                    {item.discountRate && item.discountRate.toFixed(0)}% Sale
                  </span>
                )}
              </div>
            </div>
            <div className={classes.infoSection}>
              <div className={classes.categories}>
                {isCategoryLoading ? (
                  <Loading on size="1.2rem" thickness="1" marginLeft="0" />
                ) : (
                  categoryList.map((category, index) => (
                    <div className={classes.category} key={category.id}>
                      <Link to={`/shop?collectionId=${category.id}`}>
                        {category.title}
                      </Link>
                      {index !== categoryList.length - 1 ? (
                        <span>{">"}</span>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
              <div className={classes.brandAndRatingAndNumberOfReviews}>
                <div className={classes.brand}>{item.brand}</div>
                <div className={classes.ratingAndNumberOfReviews}>
                  <StyledRating
                    name="read-only"
                    value={item.avgRating}
                    precision={0.5}
                    readOnly
                    size="small"
                  />
                  <span className={classes.numberOfReviews}>
                    {item.reviews?.length}
                  </span>
                </div>
              </div>
              <div className={classes.name}>{item.title}</div>
              <Price
                item={
                  selectedProductOrVariant &&
                  typeof selectedProductOrVariant !== "string"
                    ? selectedProductOrVariant
                    : item
                }
              />
              <div className={classes.optionsAndisSoldOut}>
                <div className={classes.sizeOrColorOptions}>
                  {item.variantOptions && item.variantOptions.length !== 0
                    ? item.variantOptions.map((variantOption: any) => {
                        const { label, options } = variantOption;
                        return (
                          <div className={classes.optionContainer} key={label}>
                            <div>
                              <span className={classes.optionLabel}>
                                {label}:{" "}
                              </span>
                              <span className={classes.selectedOption}>
                                {selectedOptions?.[label]}
                              </span>
                            </div>
                            <div className={classes.options}>
                              {options.map((option: any) => (
                                <OptionButton
                                  key={option}
                                  label={label}
                                  option={option}
                                  handleSelectOption={handleSelectOption}
                                  selectedOptions={selectedOptions}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })
                    : null}
                </div>
                <div className={classes.isSoldOut}>
                  {item?.variantOptions &&
                  item?.variantOptions.length !== 0 &&
                  selectedProductOrVariant === "CHOOSE_OPTION"
                    ? "Choose an option"
                    : item?.variantOptions &&
                      item?.variantOptions.length !== 0 &&
                      selectedProductOrVariant === "UNAVAILABLE"
                    ? "Unavailable option"
                    : selectedProductOrVariant?.isSoldOut
                    ? "Out of stock"
                    : item?.isSoldOut
                    ? "Out of stock"
                    : `${
                        selectedProductOrVariant?.quantity &&
                        selectedProductOrVariant?.quantity < 20
                          ? `Only ${selectedProductOrVariant?.quantity} left`
                          : "In stock"
                      }`}
                </div>
              </div>
              <div className={classes.addToCartContainer}>
                <div className={classes.quantityContainer}>
                  <div className={classes.quantityText}>Quantity</div>
                  <div className={classes.quantityControl}>
                    <button
                      className={classes.addOrRemoveBtn}
                      onClick={handleQuantityRemove}
                      disabled={item.isSoldOut}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      name="quantity"
                      value={quantity || ""}
                      placeholder={
                        item.isSoldOut ? "Out of Stock" : "Enter Quantity"
                      }
                      onChange={handleQuantityChange}
                      disabled={item.isSoldOut}
                      className={classes.quantity}
                    />
                    <button
                      className={classes.addOrRemoveBtn}
                      onClick={handleQuantityAdd}
                      disabled={item.isSoldOut}
                    >
                      +
                    </button>
                  </div>
                </div>
                <button
                  className={classes.addToCartBtn}
                  onClick={() => {
                    if (quantity > 0) {
                      addItemToCart();
                      toggleCart();
                    }
                  }}
                  disabled={
                    item.isSoldOut ||
                    quantity === 0 ||
                    !selectedProductOrVariant ||
                    selectedProductOrVariant === "CHOOSE_OPTION" ||
                    selectedProductOrVariant === "UNAVAILABLE" ||
                    selectedProductOrVariant.isSoldOut ||
                    quantity > selectedProductOrVariant.quantity
                  }
                >
                  {item.isSoldOut || selectedProductOrVariant.isSoldOut
                    ? "Out of Stock"
                    : selectedProductOrVariant === "UNAVAILABLE"
                    ? "Unavailable"
                    : "Add to Cart"}
                </button>
              </div>
            </div>
          </div>
          <div className={classes.lowerSection}>
            {infoTabList.length === 0 ? null : (
              <div className={classes.descriptionSection}>
                <div className={classes.descriptionTitleContainer}>
                  {infoTabList.map((infoTab: any) => (
                    <InfoTabTitle
                      key={infoTab.type}
                      infoTab={infoTab}
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                    />
                  ))}
                </div>
                <div className={classes.descriptionContentContainer}>
                  {infoTabList.map((infoTab: any) => (
                    <InfoTabContent
                      key={infoTab.type}
                      infoTab={infoTab}
                      selectedTab={selectedTab}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={classes.itemNotFound}>Item Not Found</div>
      )}
      <div className={classes.otherSection}>
        {/* <div className={classes.youMightAlsoLikeSection}>
          <div className={classes.youMightAlsoLikeTitle}>
            You Might Also Like(TBD)
          </div>
          {!isFetching ? (
            <div className={classes.youMightAlsoLikeItems}>
              {justInPreview.map((el: Item) => (
                <CollectionItem key={el.id} item={el} />
              ))}
            </div>
          ) : (
            <div>Loading...</div>
          )}
        </div> */}
        {item ? (
          <div className={classes.reviewSection}>
            <div className={classes.reviewTitle}>Customer Reviews</div>
            <div className={classes.createReview}>
              <div>Create Review</div>
              <form>
                <input
                  type="number"
                  max={5}
                  min={1}
                  {...register("rate")}
                  onChange={handleReviewInputChange}
                />
                <input
                  type="text"
                  {...register("bodyText")}
                  onChange={handleReviewInputChange}
                />
                <button type="submit" onClick={handleSubmit(onSubmit)}>
                  Submit
                </button>
              </form>
            </div>
            <div className={classes.reviewContent}>
              <div className={classes.reviewLeftSection}>
                <div>Average Rate: {averageRate}</div>
                <div>{reviewCount} reviews</div>
                <div>
                  {reviewImages.map((image: any, index: number) => (
                    <img
                      key={index}
                      // className={classes.smallImage}
                      src={image.location ? image.location : image}
                      // onClick={handleImageClick}
                      id={index.toString()}
                      style={{
                        width: "6rem",
                        height: "6rem",
                        objectFit: "cover",
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className={classes.reviewRightSection}>
                {reviews
                  ? reviews.map((review: any) => (
                      <div key={review.reviewId} className={classes.review}>
                        <div className={classes.reviewUserAndDate}>
                          <div>{review.userId}</div>
                          <div>
                            {new Date(review.created).toLocaleDateString(
                              "en-us",
                              {
                                // weekday: "long",
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </div>
                        </div>
                        <div>
                          <div>Rate: {review.rate}</div>
                          {"aidensung" === review.userId ? (
                            <>
                              {/* <button>Update</button> */}
                              <button
                                value={review.reviewId}
                                onClick={handleRemoveReview}
                              >
                                Remove
                              </button>
                            </>
                          ) : null}
                        </div>
                        {review.bodyText ? <div>{review.bodyText}</div> : null}
                        {review.imageList.length !== 0 ? (
                          <div>
                            {review.imageList.map(
                              (image: any, index: number) => (
                                <img
                                  key={index}
                                  // className={classes.smallImage}
                                  src={image.location ? image.location : image}
                                  // onClick={handleImageClick}
                                  id={index.toString()}
                                  style={{
                                    width: "4rem",
                                    height: "4rem",
                                    objectFit: "cover",
                                  }}
                                />
                              ),
                            )}
                          </div>
                        ) : null}
                      </div>
                    ))
                  : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {/* <Footer /> */}
    </div>
  );
};

export default CollectionItemDetail;
